import { createStore } from 'zustand';
import {
  UploadApiError,
  fetchStudyImages,
  patchDraftImage,
  patchStudyDetails,
  type DraftImage,
  type ImageDetails,
  type ImageStage,
  type StudyDetails,
} from '../../features/upload/api';
import { withRetry } from '../../features/upload/transfer';
import { createDetailsEditorRuntime } from '../../features/details/editor';
import { refreshDetailsViews } from '../../features/details/freshness';
import { typeFits, type ImageKind } from '../../data/imageTypes';
import { queryClient } from '../../hooks/queryClient';
import { CATALOGUE_KEYS } from '../api/freshness';
import type { ImageFieldKey, ImageFieldValues, StudyFieldValues } from '../upload/DetailsFields';
import { POLL_MS, certainKindOfDraft, detailsDelta, fieldError, fieldsOf, imageDetails, kindOf, phiHint, studyDetails, type FieldError, type FileKind } from '../upload/store';

/**
 * The details editor of a study that is already published (community): the
 * study's fields and each image's, saved as they are typed over the same
 * routes and bodies the uploader uses (PATCH /studies/:id/details, PATCH
 * /images/:id).
 *
 * Every save is a DELTA against what the server last said: a field the person
 * did not touch is never sent, so a value another person changed a moment ago
 * is not overwritten by a form that merely still shows the old one. A refusal stays
 * beside its field with what was typed, and the next change sends it again.
 *
 * One editor per page (createStudyEditor): leaving the page closes it, which
 * sends what is still waiting.
 */

/** The study head's fields the editor reads (GET /studies/:studyId). */
export interface DetailsHead {
  public_id: string;
  study_instance_uid: string;
  study_id: string | null;
  description: string | null;
  diagnosis?: string | null;
  sex?: 'M' | 'F' | 'O' | null;
  /** DICOM AS, e.g. `054Y`. */
  age?: string | null;
  /** `YYYY-MM-DD`: the study date the edition keeps (from its file, or set here). */
  study_date?: string | null;
  /** An admin or member of the study's workspace. */
  editable: boolean;
  workspace?: string | null;
  collection_id: string | null;
  collection_name: string | null;
  group_id: string | null;
  group_name: string | null;
}

/** One image of the study, in the editor. */
export interface ImageRow {
  imageId: string;
  name: string;
  kind: FileKind;
  /** What the file certainly is (the converter's word, or a scanner's format), or null: the Types it cannot be are greyed. */
  certain: ImageKind | null;
  stage: ImageStage;
  percent: number | null;
  stageError: string | null;
  fields: ImageFieldValues;
  /** The details as the server last took them: the baseline a save is measured against. */
  saved: ImageDetails;
  /** Why the server refused the last save; null once it takes one. */
  error: FieldError | null;
}

/** The key the study's saves go under; an image's is its id. */
export const STUDY = 'study';
const SAVE_DEBOUNCE_MS = 600;
const CALL_ATTEMPTS = 4;

/**
 * Whether an image's details can be edited here: published, or processed and
 * waiting for its site. One still uploading or converting is shown and not
 * edited: ingest is about to fill some of its fields, and its upload page is
 * where it is being described.
 */
export const isEditable = (r: Pick<ImageRow, 'stage'>) => r.stage === 'published' || r.stage === 'needs-details';
/** The server is still working on it: its row changes by itself. */
export const isProcessing = (r: Pick<ImageRow, 'stage'>) => !isEditable(r) && r.stage !== 'failed';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** A details body read key by key. */
const record = (v: object) => v as Record<string, unknown>;

/** A stored age (`054Y`) as the form's years. Another unit is nothing the community form can say. */
export function ageYears(as: string | null | undefined): string {
  const m = as ? /^(\d{1,3})Y?$/.exec(as.trim().toUpperCase()) : null;
  return m ? String(Number(m[1])) : '';
}

/** The study form, from the head. */
export function studyFormOf(h: Pick<DetailsHead, 'study_id' | 'description' | 'diagnosis' | 'sex' | 'age'>): StudyFieldValues {
  return { label: h.study_id ?? '', sex: h.sex ?? '', age: ageYears(h.age), diagnosis: h.diagnosis ?? '', desc: h.description ?? '' };
}

/**
 * What the client already knows the server would refuse, per study field: an
 * empty Study ID, and free text that looks like an identifier. Such a field
 * is held back from the save and the page says why under it.
 */
export function studyProblems(form: StudyFieldValues): Partial<Record<'study_id' | 'diagnosis' | 'description', string>> {
  const out: Partial<Record<'study_id' | 'diagnosis' | 'description', string>> = {};
  if (!form.label.trim()) out.study_id = 'A study ID is required.';
  const diagnosis = phiHint(form.diagnosis);
  if (diagnosis) out.diagnosis = `Remove ${diagnosis}.`;
  const description = phiHint(form.desc);
  if (description) out.description = `Remove ${description}.`;
  return out;
}

/**
 * The client's problems with the study fields the person has CHANGED: a study
 * that came without a Study ID is not told it needs one until somebody edits
 * that field.
 */
export function changedStudyProblems(form: StudyFieldValues, saved: StudyDetails): ReturnType<typeof studyProblems> {
  const now = record(studyDetails(form));
  const was = record(saved);
  return Object.fromEntries(Object.entries(studyProblems(form)).filter(([k]) => !same(now[k], was[k])));
}

/** The study fields that differ from what the server last took, less the ones the server would refuse. */
export function studyDelta(form: StudyFieldValues, saved: StudyDetails): StudyDetails {
  const now = record(studyDetails(form));
  const held = studyProblems(form);
  const was = record(saved);
  return Object.fromEntries(Object.entries(now).filter(([k, v]) => !(k in held) && !same(v, was[k]))) as StudyDetails;
}

/** An image's row, from the server's view of it. */
export function rowOf(v: DraftImage): ImageRow {
  const name = v.name ?? v.image_id;
  // The kind is the converter's word; before it has spoken, the file name's.
  const kind: FileKind = (v.stage === 'published' || v.stage === 'needs-details') && (v.kind === 'wsi' || v.kind === 'flat' || v.kind === 'document') ? v.kind : kindOf(name);
  const fields = fieldsOf(v, kind);
  return { imageId: v.image_id, name, kind, certain: certainKindOfDraft(v), stage: v.stage, percent: v.percent ?? null, stageError: v.error, fields, saved: imageDetails(fields), error: null };
}

/** The image fields that differ from what the server last took, less a Name the server would refuse. */
export function imageDelta(r: Pick<ImageRow, 'fields' | 'saved'>): ImageDetails {
  const delta = detailsDelta(r.fields, r.saved);
  if (phiHint(r.fields.specDesc)) delete delta.description;
  return delta;
}

/** The form fields each image detail is shown in. */
const IMAGE_FIELDS: Record<keyof ReturnType<typeof imageDetails>, ImageFieldKey[]> = {
  image_type: ['type'],
  site: ['site', 'siteMeaning'],
  laterality: ['laterality'],
  stain: ['stain', 'stainMeaning'],
  description: ['specDesc'],
};
const STUDY_FIELDS: Record<string, keyof StudyFieldValues> = { study_id: 'label', description: 'desc', diagnosis: 'diagnosis', sex: 'sex', age: 'age' };

/**
 * What the server now says about an image, taken into its row where the row
 * has nothing of its own to say: a detail is taken only when the form still
 * shows what the server last took. A row not editable yet takes all of it.
 */
export function adoptImage(r: ImageRow, v: DraftImage): ImageRow {
  const server = rowOf(v);
  if (!isEditable(r)) return { ...server, error: r.error };
  const now = record(imageDetails(r.fields));
  const was = record(r.saved);
  const theirs = record(server.saved);
  let fields = r.fields;
  const saved = { ...r.saved } as Record<string, unknown>;
  for (const [k, keys] of Object.entries(IMAGE_FIELDS)) {
    if (!same(now[k], was[k]) || same(was[k], theirs[k])) continue;
    fields = { ...fields, ...Object.fromEntries(keys.map((f) => [f, server.fields[f]])) };
    saved[k] = theirs[k];
  }
  return { ...r, stage: v.stage, percent: v.percent ?? null, stageError: v.error, fields, saved: saved as ImageDetails };
}

/** The same rule for the study: its head, taken where the form still shows what the server last took. */
export function adoptStudy(form: StudyFieldValues, saved: StudyDetails, head: DetailsHead): { form: StudyFieldValues; saved: StudyDetails } | null {
  const serverForm = studyFormOf(head);
  const theirs = record(studyDetails(serverForm));
  const now = record(studyDetails(form));
  const was = record(saved);
  let next = form;
  const nextSaved = { ...saved } as Record<string, unknown>;
  let changed = false;
  for (const [k, f] of Object.entries(STUDY_FIELDS)) {
    if (!same(now[k], was[k]) || same(was[k], theirs[k])) continue;
    next = { ...next, [f]: serverForm[f] };
    nextSaved[k] = theirs[k];
    changed = true;
  }
  return changed ? { form: next, saved: nextSaved as StudyDetails } : null;
}

/**
 * How many targets hold a change the server has not taken and will not take
 * as it is: refused by the server, or held back by the client (an empty Study
 * ID, identifier-like text). Done stops for these; a change merely waiting is
 * sent by Done itself.
 */
export function refusedCount(s: Pick<EditorState, 'study' | 'studySaved' | 'studyError' | 'images'>): number {
  const studyHeld = Object.keys(changedStudyProblems(s.study, s.studySaved)).length > 0;
  const images = (s.images ?? []).filter((r) => r.error || (phiHint(r.fields.specDesc) && !same(imageDetails(r.fields).description, r.saved.description)));
  return (s.studyError || studyHeld ? 1 : 0) + images.length;
}

/** What the server's refusal of a save says, and the field it is about. `body` is what was sent. */
export function refusalOf(err: unknown, body?: ImageDetails | StudyDetails): FieldError {
  if (err instanceof UploadApiError) {
    // Clearing the Type or the site of a published image (an older server says "modality").
    if (err.status === 400 && /keeps its (?:type|modality) and anatomic site/.test(err.message)) {
      const site = !!body && 'site' in body && !(body as ImageDetails).site;
      return { field: site ? 'site' : 'type', message: site ? 'A published image keeps its anatomic site.' : 'A published image keeps its type.' };
    }
    if (err.status === 400 && /study_id/.test(err.message)) return { field: 'study_id', message: 'A study ID is required.' };
    if (err.status === 403) return { field: null, message: err.message && err.message !== '403' ? err.message.charAt(0).toUpperCase() + err.message.slice(1) : 'You can no longer edit this study.' };
  }
  return fieldError(err);
}

export interface EditorState {
  studyId: string;
  study: StudyFieldValues;
  /** The study date as typed (`YYYY-MM-DD`, or empty), and as the server last took it. Not part of the uploader's form. */
  studyDate: string;
  studyDateSaved: string;
  studySaved: StudyDetails;
  studyError: FieldError | null;
  /** Null while the list is loading. */
  images: ImageRow[] | null;
  /** The list could not be had: the study is gone (404), or the server could not be reached. */
  imagesError: string | null;
  selectedId: string | null;
  /** A change is waiting to be saved, or a save is running. */
  pending: boolean;
  /** Something was saved since the editor opened. */
  saved: boolean;
  /** Start from the head, and list the images. */
  open: (head: DetailsHead) => void;
  /** A newer head (another save, another tab): taken where the form has nothing of its own. */
  takeHead: (head: DetailsHead) => void;
  setStudy: (p: Partial<StudyFieldValues>) => void;
  setStudyDate: (date: string) => void;
  setImageField: (imageId: string, k: ImageFieldKey, v: string) => void;
  select: (imageId: string) => void;
  /** The selected image's details go to every other editable image; returns how many. */
  applyToAll: () => number;
  /** Save now: one target (a field lost focus), or everything waiting (Done). */
  flush: (key?: string) => Promise<void>;
  /** The server's list of the study's images, squared with the rows. */
  sync: (images: ReadonlyArray<DraftImage>) => void;
  /** Leave: what is waiting is sent, and the views are refreshed once it lands. */
  close: () => void;
}

export type StudyEditor = ReturnType<typeof createStudyEditor>;

export function createStudyEditor(studyId: string, deps: { refresh?: (studyUid: string | undefined) => Promise<void> } = {}) {
  const refresh = deps.refresh ?? ((studyUid) => refreshDetailsViews(queryClient, CATALOGUE_KEYS, studyUid));
  /** The study's UID, from its head: the DICOMweb reads to refresh are this study's. */
  let studyUid: string | undefined;

  const store = createStore<EditorState>((set, get) => {
    const patchRow = (imageId: string, p: Partial<ImageRow> | ((r: ImageRow) => Partial<ImageRow>)) =>
      set((s) => ({ images: s.images?.map((r) => (r.imageId === imageId ? { ...r, ...(typeof p === 'function' ? p(r) : p) } : r)) ?? null }));

    const saveStudy = async (): Promise<boolean> => {
      const s = get();
      const date = s.studyDate;
      const body: StudyDetails = { ...studyDelta(s.study, s.studySaved), ...(date !== s.studyDateSaved ? { study_datetime: date || null } : {}) };
      if (!Object.keys(body).length) {
        if (s.studyError) set({ studyError: null });
        return false;
      }
      try {
        await withRetry(() => patchStudyDetails(studyId, body), { attempts: CALL_ATTEMPTS });
        const { study_datetime: _date, ...cols } = body;
        set((now) => ({ studySaved: { ...now.studySaved, ...cols }, ...('study_datetime' in body ? { studyDateSaved: date } : {}), studyError: null, saved: true }));
        return true;
      } catch (err) {
        set({ studyError: refusalOf(err, body) });
        return false;
      }
    };

    const saveImage = async (imageId: string): Promise<boolean> => {
      const r = get().images?.find((x) => x.imageId === imageId);
      if (!r || !isEditable(r)) return false;
      const body = imageDelta(r);
      if (!Object.keys(body).length) {
        if (r.error) patchRow(imageId, { error: null });
        return false;
      }
      try {
        const v = await withRetry(() => patchDraftImage(imageId, body), { attempts: CALL_ATTEMPTS });
        patchRow(imageId, (now) => ({ saved: { ...now.saved, ...body }, error: null, stage: v.stage, percent: v.percent ?? null, stageError: v.error }));
        set({ saved: true });
        return true;
      } catch (err) {
        patchRow(imageId, { error: refusalOf(err, body) });
        return false;
      }
    };

    const runtime = createDetailsEditorRuntime<DraftImage, ImageRow>({
      save: (key) => (key === STUDY ? saveStudy() : saveImage(key)),
      refresh: () => refresh(studyUid),
      onPending: (pending) => set({ pending }),
      fetchImages: async () => [...(await fetchStudyImages(studyId))],
      onImages: (images) => get().sync(images),
      onImagesError: (err) => {
        if (err instanceof UploadApiError && err.status === 404) {
          set({ imagesError: 'This study no longer exists. It may have been deleted.' });
          return true;
        }
        // Unreachable: said while nothing is listed, retried either way.
        if (!get().images) set({ imagesError: 'Could not reach the server. Trying again…' });
        return false;
      },
      rows: () => get().images,
      isProcessing,
      delayMs: SAVE_DEBOUNCE_MS,
      pollMs: POLL_MS,
    });

    return {
      studyId,
      study: { label: '', sex: '', age: '', diagnosis: '', desc: '' },
      studyDate: '',
      studyDateSaved: '',
      studySaved: {},
      studyError: null,
      images: null,
      imagesError: null,
      selectedId: null,
      pending: false,
      saved: false,
      open: (head) => {
        studyUid = head.study_instance_uid;
        const form = studyFormOf(head);
        set({ study: form, studySaved: studyDetails(form), studyDate: head.study_date ?? '', studyDateSaved: head.study_date ?? '' });
        void runtime.load();
      },
      takeHead: (head) => {
        const s = get();
        const adopted = adoptStudy(s.study, s.studySaved, head);
        if (adopted) set({ study: adopted.form, studySaved: adopted.saved });
        // The date by the same rule: taken where the form still shows what the server last took.
        const theirs = head.study_date ?? '';
        if (s.studyDate === s.studyDateSaved && theirs !== s.studyDateSaved) set({ studyDate: theirs, studyDateSaved: theirs });
      },
      setStudy: (p) => {
        set((s) => ({ study: { ...s.study, ...p } }));
        runtime.schedule(STUDY);
      },
      setStudyDate: (date) => {
        set({ studyDate: date });
        runtime.schedule(STUDY);
      },
      setImageField: (imageId, k, v) => {
        const r = get().images?.find((x) => x.imageId === imageId);
        if (!r || !isEditable(r)) return;
        patchRow(imageId, (now) => ({ fields: { ...now.fields, [k]: v } }));
        runtime.schedule(imageId);
      },
      select: (imageId) => set({ selectedId: imageId }),
      applyToAll: () => {
        const s = get();
        const src = s.images?.find((r) => r.imageId === s.selectedId);
        if (!s.images || !src) return 0;
        const others = s.images.filter((r) => r.imageId !== src.imageId && isEditable(r));
        // The Type describes how the image was made: it goes to every image but
        // one whose file certainly cannot be that Type (the uploader's rule);
        // a PDF's Type was never chosen, so it goes nowhere. The laterality goes
        // with the site it qualifies. The Name is each image's own and stays.
        const copy = (r: ImageRow): ImageRow => {
          const f = src.fields;
          const fits = src.certain !== 'document' && typeFits(f.type, r.certain);
          return { ...r, fields: { ...r.fields, site: f.site, siteMeaning: f.siteMeaning, laterality: f.laterality, stain: f.stain, stainMeaning: f.stainMeaning, ...(fits ? { type: f.type } : {}) } };
        };
        set({ images: s.images.map((r) => (others.includes(r) ? copy(r) : r)) });
        others.forEach((r) => runtime.schedule(r.imageId));
        return others.length;
      },
      flush: (key) => runtime.flush(key),
      sync: (list) => {
        const s = get();
        const byId = new Map((s.images ?? []).map((r) => [r.imageId, r]));
        const images = list.map((v) => {
          const r = byId.get(v.image_id);
          return r ? adoptImage(r, v) : rowOf(v);
        });
        const selectedId = images.some((r) => r.imageId === s.selectedId) ? s.selectedId : (images.find(isEditable) ?? images[0])?.imageId ?? null;
        set({ images, imagesError: null, selectedId });
      },
      close: () => runtime.close(),
    };
  });
  return store;
}
